import React, { useEffect, useState, useMemo, useCallback } from 'react'
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Input,
  Button,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  Badge,
  UncontrolledTooltip,
} from 'reactstrap'
import BreadCrumb from 'Components/Common/BreadCrumb'
import { toast } from 'react-toastify'
import { APIClient } from 'helpers/api_helper'
import {
  fetchTranslationOverrides,
  updateTranslationOverrides,
  deleteTranslationOverride,
} from 'helpers/backend_helper'
import classnames from 'classnames'
import { getPermissions } from 'lib/permissionUtils'

// ---------------------------------------------------------------------------
// Translation key definitions grouped by section (for better UX)
// ---------------------------------------------------------------------------
interface KeyDef {
  key: string
  description: string
}

interface KeyGroup {
  title: string
  icon: string
  keys: KeyDef[]
}

const TRANSLATION_GROUPS: KeyGroup[] = [
  {
    title: 'Genel',
    icon: 'mdi mdi-cog-outline',
    keys: [
      { key: 'title', description: 'Uygulama baslik' },
      { key: 'cancelButton', description: 'Vazgec butonu' },
      { key: 'acceptButton', description: 'Onayla butonu' },
      { key: 'backAlt', description: 'Geri butonu alt' },
      { key: 'homeButton', description: 'Anasayfaya don' },
      { key: 'logoutButton', description: 'Cikis yap' },
      { key: 'cancelText', description: 'Iptal metni' },
      { key: 'saveText', description: 'Kaydet metni' },
      { key: 'closeButton', description: 'Kapat butonu' },
      { key: 'resetButton', description: 'Sifirla butonu' },
      { key: 'loadingText', description: 'Yukleniyor metni' },
      { key: 'processing', description: 'Isleniyor metni' },
      { key: 'remainingTime', description: 'Kalan sure' },
      { key: 'backToMenu', description: 'Menuye don' },
      { key: 'orText', description: 'veya metni' },
      { key: 'cancel', description: 'Iptal (emoji)' },
      { key: 'confirm', description: 'Onayla (emoji)' },
    ],
  },
  {
    title: 'KVKK / Modal',
    icon: 'mdi mdi-shield-check',
    keys: [
      { key: 'modalTitle', description: 'KVKK modal baslik' },
      { key: 'modalBody', description: 'KVKK modal icerik' },
    ],
  },
  {
    title: 'Miktar Secimi',
    icon: 'mdi mdi-numeric',
    keys: [
      { key: 'selectPrompt', description: 'Kac adet yazdirilacak' },
      { key: 'unitLabel', description: 'Birim (adet)' },
      { key: 'outputCount', description: 'Cikti alinacak' },
      { key: 'printCountLabel', description: 'Baski adedi' },
    ],
  },
  {
    title: 'Odeme',
    icon: 'mdi mdi-credit-card-outline',
    keys: [
      { key: 'paymentPrompt', description: 'Odeme yontemi sec' },
      { key: 'paymentSubheader', description: 'Odeme alt baslik' },
      { key: 'cashButton', description: 'Nakit butonu' },
      { key: 'creditButton', description: 'Kredi karti butonu' },
      { key: 'amountLabel', description: 'Tutar etiketi' },
      { key: 'paidLabel', description: 'Odenen etiketi' },
      { key: 'remainingLabel', description: 'Kalan etiketi' },
      { key: 'cardAlt', description: 'Kart odemesi alt' },
      { key: 'posInstruction', description: 'POS talimati' },
      { key: 'paymentSuccessTitle', description: 'Odeme basarili baslik' },
      { key: 'paymentSuccessBody', description: 'Odeme basarili aciklama' },
      { key: 'confirmPaymentButton', description: 'Odeme tamamlandi butonu' },
      { key: 'instruction1', description: 'Nakit talimat 1' },
      { key: 'instruction2', description: 'Nakit talimat 2' },
      { key: 'instruction3', description: 'Nakit talimat 3' },
      { key: 'cashSimulationTitle', description: 'Nakit simulasyon baslik' },
    ],
  },
  {
    title: 'Promosyon Kodu',
    icon: 'mdi mdi-ticket-percent',
    keys: [
      { key: 'promoQuestion', description: 'Promo kodu soru' },
      { key: 'promoPlaceholder', description: 'Promo placeholder' },
      { key: 'applyButton', description: 'Uygula butonu' },
      { key: 'promoSuccess', description: 'Promo basarili' },
      { key: 'promoInvalid', description: 'Promo gecersiz' },
      { key: 'discountApplied', description: 'Indirim uygulandi' },
      { key: 'completedButton', description: 'Baski ekranina git' },
    ],
  },
  {
    title: 'Fotograf / Kamera',
    icon: 'mdi mdi-camera',
    keys: [
      { key: 'captureButton', description: 'Cekime basla butonu' },
      { key: 'holdStillWarning', description: 'Hareket etmeyin uyarisi (ekran ust/alt bant)' },
      { key: 'retakeButton', description: 'Tekrar cek butonu' },
      { key: 'cameraBackButton', description: 'Kameraya don' },
      { key: 'samplePlaceholder', description: 'Ornek placeholder' },
      { key: 'takePhotoInstruction', description: 'Foto cekme talimati' },
      { key: 'cameraAccessDenied', description: 'Kamera erisim reddedildi' },
      { key: 'getReady', description: 'Hazir ol' },
      { key: 'photoCaptured', description: 'Foto cekildi' },
      { key: 'retakeLeft', description: 'Yeniden cek' },
      { key: 'onlyEditingAllowed', description: 'Sadece duzenleme' },
      { key: 'retakeRights', description: 'Yeniden cekim hakki' },
      { key: 'rightsLeft', description: 'Hak kaldi' },
      { key: 'sameBackground', description: 'Ayni sahne icin' },
      { key: 'retakeInfoTooltip', description: 'Yeniden cekim bilgi' },
      { key: 'selectPhotoToStyle', description: 'Foto secin uyari' },
      { key: 'startShootingWarning', description: 'Cekim baslat uyari' },
      { key: 'noPhotosFound', description: 'Foto bulunamadi' },
      { key: 'noPhotosAvailable', description: 'Foto mevcut degil' },
      { key: 'photosAfterCapture', description: 'Cekim sonrasi mesaj' },
      { key: 'processingYourPhoto', description: 'Foto isleniyor' },
    ],
  },
  {
    title: 'Sahne / Arka Plan',
    icon: 'mdi mdi-image-multiple',
    keys: [
      { key: 'backgroundsTitle', description: 'Sahne sec baslik' },
      { key: 'noBackground', description: 'Sahne yok' },
      { key: 'selectBackgroundFirst', description: 'Once sahne sec' },
      { key: 'backgroundSelected', description: 'Sahne secildi' },
      { key: 'selectBackgroundWarning', description: 'Sahne uyari' },
      { key: 'confirmBackgroundSelection', description: 'Sahne onay baslik' },
      { key: 'confirmBackgroundMessage', description: 'Sahne onay mesaj' },
      { key: 'selectionFinal', description: 'Secim degistirilemez' },
      { key: 'noBackgroundSelected', description: 'Sahne secilmedi' },
      { key: 'selectFromPanel', description: 'Panelden sahne sec' },
      { key: 'removingBackground', description: 'Sahne kaldiriliyor' },
      { key: 'backgroundRemovalReady', description: 'Sahne kaldirma hazir' },
      { key: 'loadingModel', description: 'Model yukleniyor' },
      { key: 'backgroundRemovalNotReady', description: 'Sahne kaldirma hazir degil' },
      { key: 'backgroundRemovalFailed', description: 'Sahne kaldirma basarisiz' },
      { key: 'removeBgAgain', description: 'Sahneyi tekrar kaldir' },
      { key: 'removeBg', description: 'Sahneyi kaldir' },
    ],
  },
  {
    title: 'Duzenleme (Filtre/Sticker/Metin)',
    icon: 'mdi mdi-auto-fix',
    keys: [
      { key: 'decorateTitle', description: 'Tarzini yarat baslik' },
      { key: 'effectTab', description: 'Filtre sekmesi' },
      { key: 'stickerTab', description: 'Aksesuar sekmesi' },
      { key: 'textTab', description: 'Metin sekmesi' },
      { key: 'removeButton', description: 'Kaldir butonu' },
      { key: 'proceedButton', description: 'Devam et butonu' },
      { key: 'selectEffectTitle', description: 'Filtre sec baslik' },
      { key: 'selectStickerTitle', description: 'Sticker sec baslik' },
      { key: 'addTextTitle', description: 'Metin ekle baslik' },
      { key: 'textPlaceholder', description: 'Metin placeholder' },
      { key: 'noFilter', description: 'Filtre yok' },
      { key: 'typeAwesome', description: 'Harika birsey yaz' },
      { key: 'sizeLabel', description: 'Boyut etiketi' },
      { key: 'chooseColor', description: 'Renk sec' },
      { key: 'fontTab', description: 'Font sekmesi' },
      { key: 'styleTab', description: 'Stil sekmesi' },
      { key: 'boldText', description: 'Kalin metin' },
      { key: 'italicText', description: 'Egik metin' },
      { key: 'underText', description: 'Alti cizili' },
      { key: 'addTextButton', description: 'Metin ekle butonu' },
      { key: 'removeTextButton', description: 'Metin kaldir butonu' },
      { key: 'selectPhotoFirst', description: 'Once foto secin' },
      { key: 'chooseFromThumbnails', description: 'Kucuk resimden sec' },
    ],
  },
  // NOT: "Kategoriler" grubu artık backend'den dinamik olarak doldurulur (aşağıda state ile).
  // Bu statik liste kaldırıldı.
  {
    title: 'Efektler',
    icon: 'mdi mdi-sparkles',
    keys: [
      { key: 'glitterEffect', description: 'Parlak efekt' },
      { key: 'sepiaEffect', description: 'Sepya efekt' },
      { key: 'blackWhiteEffect', description: 'Siyah-Beyaz efekt' },
    ],
  },
  {
    title: 'Baski & Paylasim',
    icon: 'mdi mdi-printer',
    keys: [
      { key: 'printShareTitle', description: 'Baski paylasim baslik' },
      { key: 'emailAction', description: 'E-posta gonder' },
      { key: 'smsAction', description: 'SMS gonder' },
      { key: 'printAction', description: 'Kopya baski' },
      { key: 'preparingPrint', description: 'Baski hazirlaniyor' },
    ],
  },
  {
    title: 'E-posta Modal',
    icon: 'mdi mdi-email-outline',
    keys: [
      { key: 'emailModalTitle', description: 'E-posta modal baslik' },
      { key: 'emailPlaceholder', description: 'E-posta placeholder' },
      { key: 'sendButton', description: 'Gonder butonu' },
      { key: 'sending', description: 'Gonderiliyor' },
      { key: 'emailSent', description: 'E-posta gonderildi' },
      { key: 'emailError', description: 'E-posta hata' },
      { key: 'invalidEmail', description: 'Gecersiz e-posta' },
    ],
  },
  {
    title: 'SMS Modal',
    icon: 'mdi mdi-message-text-outline',
    keys: [
      { key: 'smsModalTitle', description: 'SMS modal baslik' },
      { key: 'smsSent', description: 'SMS gonderildi' },
      { key: 'phonePlaceholder', description: 'Telefon placeholder' },
      { key: 'invalidPhone', description: 'Gecersiz telefon' },
      { key: 'smsError', description: 'SMS hata' },
      { key: 'deleteKey', description: 'Silme tusu (klavye)' },
      { key: 'spaceKey', description: 'Bosluk tusu etiketi (ekran klavyesi)' },
    ],
  },
  {
    title: 'Ek Sure Uyarisi',
    icon: 'mdi mdi-timer-sand',
    keys: [
      { key: 'extraTimeTitle', description: 'Ek sure modal baslik' },
      { key: 'extraTimeBody', description: 'Ek sure modal aciklama' },
      { key: 'extraTimeYes', description: 'Evet devam et butonu' },
      { key: 'extraTimeNo', description: 'Hayir bitir butonu (2026-07-31)' },
    ],
  },
  {
    title: 'Kamera Uyarilari',
    icon: 'mdi mdi-camera-off',
    keys: [
      { key: 'cameraWarmingUp', description: 'Kamera hazirlaniyor - GECICI, kendiliginden acilir (2026-08-02)' },
      { key: 'cameraNotReadyService', description: 'Kamera calismiyor - hizmet baslatilamaz' },
      { key: 'cameraNotReadyPayment', description: 'Kamera hizmet disi - odeme alinamaz' },
      { key: 'cameraDarkHint', description: 'Gorevli notu - lens kapagi / karanlik oda' },
    ],
  },
  {
    // Kabinler ekranindaki "Bakima Al" butonu bu metinleri gosterir (2026-08-24)
    title: 'Bakim Modu',
    icon: 'mdi mdi-wrench-outline',
    keys: [
      { key: 'maintenanceBadge', description: 'Hizmet kartindaki kucuk rozet' },
      { key: 'maintenanceServiceMsg', description: 'Bakimdaki hizmete dokununca cikan mesaj' },
      { key: 'maintenanceAllTitle', description: 'TUM hizmetler bakimdayken tam ekran baslik' },
      { key: 'maintenanceAllBody', description: 'TUM hizmetler bakimdayken tam ekran metin' },
    ],
  },
  {
    title: 'Telefondan Baski',
    icon: 'mdi mdi-cellphone-arrow-down',
    keys: [
      { key: 'phoneToPrintTitle', description: 'Telefondan foto yukle baslik' },
      { key: 'phoneToPrintStep1', description: 'Adim 1 - QR oku' },
      { key: 'phoneToPrintStep2', description: 'Adim 2 - Foto yukle' },
      { key: 'phoneToPrintStep3', description: 'Adim 3 - Onizle ve onayla' },
      { key: 'qrPreparingText', description: 'QR hazirlaniyor' },
      { key: 'uploadingPhoto', description: 'Foto yukleniyor' },
      { key: 'clearAndRetake', description: 'Sil ve tekrar yukle' },
      { key: 'confirmAndPrint', description: 'Onayla ve yazdir' },
    ],
  },
  {
    title: 'Photoroll',
    icon: 'mdi mdi-filmstrip',
    keys: [
      { key: 'photorollTitle', description: 'Photoroll baslik' },
      { key: 'photorollPoseOf', description: 'Poz sayaci ({n} ve {total} yer tutucularini koruyun)' },
      { key: 'photorollRestart', description: 'Bastan cek butonu' },
      { key: 'photorollReviewTitle', description: 'Onizleme baslik' },
      { key: 'photorollPreparing', description: 'Hazirlaniyor metni' },
      { key: 'photorollChooseEffect', description: 'Efekt sec metni' },
      { key: 'photorollPrint', description: 'Baski yap butonu' },
      { key: 'photorollRestartTitle', description: 'Bastan cek modal baslik' },
      { key: 'photorollRestartBody', description: 'Bastan cek modal aciklama ({n} yer tutucusunu koruyun)' },
      { key: 'photorollRestartCancel', description: 'Vazgec butonu' },
    ],
  },
  {
    title: 'Cikis Sayfasi',
    icon: 'mdi mdi-exit-to-app',
    keys: [
      { key: 'exitTitle', description: 'Cikis baslik' },
      { key: 'exitReminder', description: 'Cikis hatirlatma' },
      { key: 'pickupReminder', description: 'Baski alma hatirlatma' },
      { key: 'exitEmoji', description: 'Cikis emoji' },
      { key: 'exitSubtext', description: 'Cikis alt metin' },
    ],
  },
]

// Kategori/efekt adını translation key'e çevirir.
// Örn: "Çocuklar için" → "cocuklarIcinCategory", "Parıltı" → "pariltiEffect"
// Pratik: türkçe aksan sadeleştir, boşlukları kaldır, camelCase yap, verilen suffix'i ekle.
const slugifyWithSuffix = (name: string, suffix: string): string => {
  const trMap: Record<string, string> = {
    'ç': 'c', 'Ç': 'c', 'ğ': 'g', 'Ğ': 'g', 'ı': 'i', 'İ': 'i',
    'ö': 'o', 'Ö': 'o', 'ş': 's', 'Ş': 's', 'ü': 'u', 'Ü': 'u',
  }
  const ascii = String(name || '')
    .split('')
    .map((c) => trMap[c] ?? c)
    .join('')
    .replace(/[^a-zA-Z0-9\s]/g, ' ')
    .trim()
  if (!ascii) return ''
  const parts = ascii.split(/\s+/)
  const camel = parts
    .map((p, i) => (i === 0 ? p.toLowerCase() : p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()))
    .join('')
  return camel + suffix
}

const slugifyCategoryKey = (name: string): string => slugifyWithSuffix(name, 'Category')
const slugifyEffectKey = (name: string): string => slugifyWithSuffix(name, 'Effect')

// ---------------------------------------------------------------------------
// Language config
// ---------------------------------------------------------------------------
interface LangTab {
  code: string
  label: string
  flag: string
}

const LANGUAGES: LangTab[] = [
  { code: 'tr', label: 'Turkce', flag: '🇹🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', label: 'Francais', flag: '🇫🇷' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const Translations = () => {
  document.title = 'Translations | SocialBooth - Admin Dashboard'

  const [loading, setLoading] = useState<boolean>(true)
  const [saving, setSaving] = useState<boolean>(false)
  const [activeLang, setActiveLang] = useState<string>('tr')
  const [search, setSearch] = useState<string>('')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(TRANSLATION_GROUPS.map((g) => g.title))
  )

  // defaults[lang][key] = hardcoded default value (from frontend translations.tsx)
  const [defaults, setDefaults] = useState<Record<string, Record<string, string>>>({
    tr: {}, en: {}, de: {}, fr: {}, ar: {},
  })

  // overrides[lang][key] = value (from backend)
  const [overrides, setOverrides] = useState<Record<string, Record<string, string>>>({
    tr: {}, en: {}, de: {}, fr: {}, ar: {},
  })

  // local edits (only current session changes, tracks dirty state)
  const [localEdits, setLocalEdits] = useState<Record<string, Record<string, string>>>({
    tr: {}, en: {}, de: {}, fr: {}, ar: {},
  })

  // Dinamik olarak backend'den çekilen kategoriler (arkaplan kategorileri)
  const [dynamicCategories, setDynamicCategories] = useState<string[]>([])
  // Dinamik efekt/filter isimleri — admin override için slugified key üretmek için.
  const [dynamicEffects, setDynamicEffects] = useState<string[]>([])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res: any = await new APIClient().get('/api/v1/backgrounds/categories', null)
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : []
        if (!cancelled) {
          // Dedupe + trim + boşları çıkar + alfabetik
          const clean = Array.from(
            new Set(list.map((c: any) => String(c || '').trim()).filter(Boolean))
          ).sort((a, b) => (a as string).localeCompare(b as string, 'tr'))
          setDynamicCategories(clean as string[])
        }
      } catch (e) {
        console.warn('[Translations] Kategoriler alınamadı:', (e as any)?.message || e)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Filter/Effect isimlerini backend'den çek
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res: any = await new APIClient().get('/api/v1/effects/getAllFilters', null)
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : []
        if (!cancelled) {
          const clean = Array.from(
            new Set(list.map((e: any) => String(e?.name || '').trim()).filter(Boolean))
          ).sort((a, b) => (a as string).localeCompare(b as string, 'tr'))
          setDynamicEffects(clean as string[])
        }
      } catch (e) {
        console.warn('[Translations] Efektler alınamadı:', (e as any)?.message || e)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // TRANSLATION_GROUPS + dinamik "Kategoriler" + dinamik "Efektler"
  const groupsWithCategories = useMemo(() => {
    const categoryGroup = {
      title: 'Kategoriler',
      icon: 'mdi mdi-tag-multiple',
      keys: [
        { key: 'allCategory', description: 'Tümü (tüm kategoriler için etiket)' },
        ...dynamicCategories.map((name) => ({
          key: slugifyCategoryKey(name),
          description: name,
        })),
      ].filter((k) => !!k.key),
    }
    // TRANSLATION_GROUPS içindeki statik "Efektler" grubunu dinamikle zenginleştir:
    // hardcoded 3 anahtar + DB'den gelen ek filtre isimleri (slugified).
    const dynamicEffectKeys = dynamicEffects
      .map((name) => ({ key: slugifyEffectKey(name), description: name }))
      .filter((k) => !!k.key)
    const seen = new Set<string>()
    const enriched = TRANSLATION_GROUPS.map((g) => {
      if (g.title !== 'Efektler') return g
      const merged = [...g.keys, ...dynamicEffectKeys].filter((k) => {
        if (seen.has(k.key)) return false
        seen.add(k.key)
        return true
      })
      return { ...g, keys: merged }
    })
    return [...enriched, categoryGroup]
  }, [dynamicCategories, dynamicEffects])

  // Load defaults + overrides from backend
  const loadOverrides = useCallback(async () => {
    try {
      setLoading(true)
      const [overridesRes, defaultsRes]: any[] = await Promise.all([
        fetchTranslationOverrides(),
        new APIClient().get('/api/v1/translations/defaults', null)
      ])

      // Axios interceptor response.data'yı unwrap eder:
      // overridesRes = { success, data: {tr:{}, en:{}, ...} }
      // defaultsRes = { success, data: {tr:{...}, en:{...}, ...} }
      if (overridesRes?.success) setOverrides(overridesRes.data)
      else if (overridesRes?.data) setOverrides(overridesRes.data)

      if (defaultsRes?.success) setDefaults(defaultsRes.data)
      else if (defaultsRes?.data) setDefaults(defaultsRes.data)
    } catch (e) {
      toast.error('Ceviri verileri yuklenemedi.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadOverrides()
  }, [loadOverrides])

  // Current value for a key (local edit > override > empty)
  const getValue = (lang: string, key: string): string => {
    if (localEdits[lang]?.[key] !== undefined) return localEdits[lang][key]
    if (overrides[lang]?.[key] !== undefined) return overrides[lang][key]
    return ''
  }

  // Handle input change
  const handleChange = (key: string, value: string) => {
    setLocalEdits((prev) => ({
      ...prev,
      [activeLang]: {
        ...prev[activeLang],
        [key]: value,
      },
    }))
  }

  // Count of dirty (changed) items for current lang
  const dirtyCount = useMemo(() => {
    const edits = localEdits[activeLang] || {}
    return Object.keys(edits).filter((key) => {
      const current = overrides[activeLang]?.[key] || ''
      return edits[key] !== current
    }).length
  }, [localEdits, overrides, activeLang])

  // Total overrides count for badge
  const overrideCount = useMemo(() => {
    return Object.keys(overrides[activeLang] || {}).length
  }, [overrides, activeLang])

  // 🔴 2026-08-17: bu sayfa yetkiyi hiç okumuyordu VE backend uçları
  // (PUT / DELETE /api/v1/translations) generic verifyAdmin kullanıyordu —
  // sadece GÖRÜNTÜLEME yetkisi olan operatör kabin metinlerini
  // değiştirebiliyordu. Backend artık check("Translations", ...) istiyor;
  // burası da düğmeleri gizler.
  const permissions = useMemo(() => getPermissions('Translations'), [])

  // Save changes
  const handleSave = async () => {
    const edits = localEdits[activeLang] || {}
    // Değişmiş her anahtarı gönder. Boş string ise backend override'ı siler.
    const changes: Record<string, string> = {}
    for (const [key, value] of Object.entries(edits)) {
      const current = overrides[activeLang]?.[key] || ''
      if (value !== current) {
        changes[key] = value
      }
    }

    if (Object.keys(changes).length === 0) {
      toast.info('Degisiklik bulunamadi.')
      return
    }

    try {
      setSaving(true)
      const response: any = await updateTranslationOverrides(activeLang, changes)
      // Interceptor unwrap: response = { success, msg, data }
      if (response?.success) {
        toast.success(response.msg || 'Ceviriler kaydedildi.')
        // Update overrides with saved data
        setOverrides((prev) => ({
          ...prev,
          [activeLang]: { ...prev[activeLang], ...changes },
        }))
        // Clear local edits for saved keys
        setLocalEdits((prev) => {
          const updated = { ...prev[activeLang] }
          for (const key of Object.keys(changes)) {
            delete updated[key]
          }
          return { ...prev, [activeLang]: updated }
        })
      } else {
        toast.error(response?.msg || 'Ceviriler kaydedilemedi.')
      }
    } catch (e: any) {
      toast.error(typeof e === 'string' ? e : 'Ceviriler kaydedilemedi.')
    } finally {
      setSaving(false)
    }
  }

  // Remove a single override
  const handleRemoveOverride = async (key: string) => {
    try {
      const response: any = await deleteTranslationOverride(activeLang, key)
      if (response?.success) {
        toast.success(`${key} override silindi.`)
        setOverrides((prev) => {
          const updated = { ...prev[activeLang] }
          delete updated[key]
          return { ...prev, [activeLang]: updated }
        })
        setLocalEdits((prev) => {
          const updated = { ...prev[activeLang] }
          delete updated[key]
          return { ...prev, [activeLang]: updated }
        })
      }
    } catch (e) {
      toast.error('Override silinemedi.')
    }
  }

  // Toggle group expand/collapse
  const toggleGroup = (title: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(title)) {
        next.delete(title)
      } else {
        next.add(title)
      }
      return next
    })
  }

  // Get default value for a key
  const getDefault = (lang: string, key: string): string => {
    return defaults[lang]?.[key] || ''
  }

  // Filter groups by search (searches key, description, default, and override)
  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groupsWithCategories
    const q = search.toLowerCase()
    return groupsWithCategories.map((group) => ({
      ...group,
      keys: group.keys.filter(
        (k) =>
          k.key.toLowerCase().includes(q) ||
          k.description.toLowerCase().includes(q) ||
          getDefault(activeLang, k.key).toLowerCase().includes(q) ||
          getValue(activeLang, k.key).toLowerCase().includes(q)
      ),
    })).filter((g) => g.keys.length > 0)
  }, [search, activeLang, overrides, localEdits, groupsWithCategories])

  // Tüm anahtarlar (dinamik kategoriler dahil) — üst bilgi için
  const allKeysCount = useMemo(
    () => groupsWithCategories.reduce((sum, g) => sum + g.keys.length, 0),
    [groupsWithCategories]
  )

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb title='Translations' pageTitle='Settings' />

          {/* Language tabs */}
          <Card>
            <CardBody className='pb-0'>
              <Nav tabs className='nav-tabs-custom nav-justified'>
                {LANGUAGES.map((lang) => (
                  <NavItem key={lang.code}>
                    <NavLink
                      className={classnames({ active: activeLang === lang.code })}
                      onClick={() => setActiveLang(lang.code)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className='me-1'>{lang.flag}</span> {lang.label}
                      {Object.keys(overrides[lang.code] || {}).length > 0 && (
                        <Badge color='info' pill className='ms-2'>
                          {Object.keys(overrides[lang.code] || {}).length}
                        </Badge>
                      )}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            </CardBody>
          </Card>

          {/* Search + Stats */}
          <Row className='mb-3'>
            <Col md={6}>
              <div className='search-box'>
                <Input
                  type='text'
                  placeholder='Anahtar veya aciklama ile ara...'
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className='form-control'
                />
                <i className='ri-search-line search-icon'></i>
              </div>
            </Col>
            <Col md={6} className='d-flex align-items-center justify-content-end gap-3'>
              <span className='text-muted'>
                <i className='mdi mdi-key me-1'></i>
                {allKeysCount} anahtar
              </span>
              <span className='text-info'>
                <i className='mdi mdi-pencil me-1'></i>
                {overrideCount} override
              </span>
              {dirtyCount > 0 && (
                <Badge color='warning'>
                  {dirtyCount} kaydedilmemis degisiklik
                </Badge>
              )}
            </Col>
          </Row>

          {loading ? (
            <div className='text-center py-5'>
              <Spinner color='primary' />
              <p className='mt-2 text-muted'>Yukleniyor...</p>
            </div>
          ) : (
            <>
              {filteredGroups.map((group) => (
                <Card key={group.title} className='mb-2'>
                  <CardHeader
                    className='d-flex align-items-center justify-content-between'
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleGroup(group.title)}
                  >
                    <h5 className='card-title mb-0'>
                      <i className={`${group.icon} me-2`}></i>
                      {group.title}
                      <Badge color='soft-secondary' className='ms-2'>
                        {group.keys.length}
                      </Badge>
                    </h5>
                    <i
                      className={`mdi ${
                        expandedGroups.has(group.title)
                          ? 'mdi-chevron-up'
                          : 'mdi-chevron-down'
                      } fs-5`}
                    ></i>
                  </CardHeader>
                  {expandedGroups.has(group.title) && (
                    <CardBody className='p-0'>
                      <div className='table-responsive'>
                        <table className='table table-hover table-striped mb-0 align-middle'>
                          <thead className='table-light'>
                            <tr>
                              <th style={{ width: '15%' }}>Anahtar</th>
                              <th style={{ width: '30%' }}>Mevcut Deger</th>
                              <th style={{ width: '35%' }}>Yeni Deger (Override)</th>
                              <th style={{ width: '10%' }} className='text-center'>Durum</th>
                              <th style={{ width: '10%' }} className='text-center'>Islem</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.keys.map((keyDef) => {
                              const val = getValue(activeLang, keyDef.key)
                              const defaultVal = getDefault(activeLang, keyDef.key)
                              const hasOverride = !!overrides[activeLang]?.[keyDef.key]
                              const isDirty =
                                localEdits[activeLang]?.[keyDef.key] !== undefined &&
                                localEdits[activeLang][keyDef.key] !==
                                  (overrides[activeLang]?.[keyDef.key] || '')

                              return (
                                <tr key={keyDef.key}>
                                  <td>
                                    <code className='text-primary small'>{keyDef.key}</code>
                                    <br />
                                    <small className='text-muted' style={{ fontSize: '0.7rem' }}>{keyDef.description}</small>
                                  </td>
                                  <td>
                                    <div className='bg-light rounded px-2 py-1 small text-dark' style={{ maxHeight: 60, overflow: 'auto', wordBreak: 'break-word' }}>
                                      {defaultVal || <span className='text-muted fst-italic'>-</span>}
                                    </div>
                                  </td>
                                  <td>
                                    <Input
                                      type={defaultVal.length > 50 ? 'textarea' : 'text'}
                                      rows={defaultVal.length > 50 ? 2 : undefined}
                                      bsSize='sm'
                                      value={val}
                                      placeholder={defaultVal || 'Deger girin...'}
                                      onChange={(e) => handleChange(keyDef.key, e.target.value)}
                                      className={isDirty ? 'border-warning' : ''}
                                      // Kaydet düğmesini gizleyip alanı açık bırakmak yanıltıcı
                                      // olurdu: kullanıcı yazar, sonra kaydedemez.
                                      disabled={!permissions.canEdit}
                                    />
                                  </td>
                                  <td className='text-center'>
                                    {hasOverride && permissions.canDelete && (
                                      <Badge color='info' pill style={{ fontSize: '0.65rem' }}>override</Badge>
                                    )}
                                    {isDirty && (
                                      <Badge color='warning' pill style={{ fontSize: '0.65rem' }}>degisti</Badge>
                                    )}
                                  </td>
                                  <td className='text-center'>
                                    {hasOverride && (
                                      <>
                                        <Button
                                          color='danger'
                                          size='sm'
                                          outline
                                          id={`del-${keyDef.key}`}
                                          onClick={() => handleRemoveOverride(keyDef.key)}
                                        >
                                          <i className='mdi mdi-delete-outline'></i>
                                        </Button>
                                        <UncontrolledTooltip target={`del-${keyDef.key}`}>
                                          Override sil (varsayilana don)
                                        </UncontrolledTooltip>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CardBody>
                  )}
                </Card>
              ))}

              {/* Save button */}
              <div className='d-flex gap-2 mb-4 sticky-bottom bg-white py-3 border-top'>
                {!permissions.canEdit && (
                  <div className='alert alert-info mb-0 py-2 px-3'>
                    <i className='mdi mdi-eye-outline me-1'></i>
                    Bu sayfada yalnizca goruntuleme yetkiniz var.
                  </div>
                )}
                {permissions.canEdit && (
                <>
                <Button
                  color='success'
                  onClick={handleSave}
                  disabled={saving || dirtyCount === 0}
                  size='lg'
                >
                  {saving ? (
                    <>
                      <Spinner size='sm' className='me-2' />
                      Kaydediliyor...
                    </>
                  ) : (
                    <>
                      <i className='mdi mdi-content-save me-1'></i>
                      Kaydet ({dirtyCount} degisiklik)
                    </>
                  )}
                </Button>
                <Button
                  color='secondary'
                  outline
                  onClick={() =>
                    setLocalEdits((prev) => ({ ...prev, [activeLang]: {} }))
                  }
                  disabled={saving || dirtyCount === 0}
                >
                  <i className='mdi mdi-undo me-1'></i>
                  Degisiklikleri Geri Al
                </Button>
                </>
                )}
              </div>
            </>
          )}
        </Container>
      </div>
    </React.Fragment>
  )
}

export default Translations
